import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Menu, Plus, TextInput } from '@opendate-app/setlist';

const DEBOUNCE_MS = 250;
const BLUR_CLEAR_MS = 150;
const NO_MATCHES = { key: 'no-matches', label: 'No matches', disabled: true };
const defaultCreateLabel = (term) => `Create "${term}"`;

const SearchPicker = ({ label, placeholder, value, onChange, search, freeText, required, hint, onCreate, createLabel }) => {
  const [text, setText] = useState('');
  const [results, setResults] = useState(null);
  const [open, setOpenState] = useState(false);
  const wrapperRef = useRef(null);
  const timerRef = useRef(null);
  const currentTermRef = useRef('');
  const allowMenuFocusRef = useRef(false);
  const typingRef = useRef(false);
  const blurTimerRef = useRef(null);
  const creatingRef = useRef(false);
  const valueRef = useRef(value);
  valueRef.current = value;

  const inputNode = () => (wrapperRef.current ? wrapperRef.current.querySelector('input') : null);

  const setOpen = useCallback((next) => {
    if (!next) allowMenuFocusRef.current = false;
    setOpenState(next);
  }, []);

  useEffect(() => () => {
    clearTimeout(timerRef.current);
    clearTimeout(blurTimerRef.current);
    currentTermRef.current = null;
  }, []);

  useEffect(() => {
    const typedIntoSelection = typingRef.current && !value;
    typingRef.current = false;
    const display = freeText ? (value || '') : ((value && value.label) || '');
    if (typedIntoSelection || (freeText && display === text)) return;
    clearTimeout(timerRef.current);
    currentTermRef.current = '';
    setResults(null);
    setOpen(false);
    setText(display);
  }, [value]);

  const openIfFocused = (options) => {
    setResults(options);
    if (document.activeElement === inputNode()) setOpen(true);
  };

  const runSearch = useCallback((term) => {
    currentTermRef.current = term;
    clearTimeout(timerRef.current);
    if (term.trim() === '') {
      setResults(null);
      setOpen(false);
      return;
    }
    timerRef.current = setTimeout(() => {
      search(term).then((options) => {
        if (currentTermRef.current !== term) return;
        openIfFocused(options);
      }).catch(() => {
        if (currentTermRef.current !== term) return;
        openIfFocused([]);
      });
    }, DEBOUNCE_MS);
  }, [search]);

  const handleChange = (event) => {
    const next = event.target.value;
    setText(next);
    if (freeText) {
      onChange(next);
    } else if (value) {
      typingRef.current = true;
      onChange(null);
    }
    runSearch(next);
  };

  const pick = (option) => {
    currentTermRef.current = '';
    clearTimeout(timerRef.current);
    clearTimeout(blurTimerRef.current);
    setResults(null);
    setOpen(false);
    setText('');
    if (freeText) {
      onChange(option.label);
    } else {
      onChange({ value: option.value, label: option.label });
    }
  };

  const create = (term) => {
    if (creatingRef.current) return;
    const result = onCreate(term);
    if (result && typeof result.then === 'function') {
      creatingRef.current = true;
      result.then(
        () => { creatingRef.current = false; },
        () => { creatingRef.current = false; },
      );
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      setOpen(false);
    } else if (event.key === 'Tab') {
      setOpen(false);
    } else if (event.key === 'ArrowDown' && open) {
      const input = inputNode();
      const menu = input && document.body.querySelector(`[role="menu"][aria-labelledby="${input.id}"]`);
      const first = menu && menu.querySelector('[role="menuitem"]:not([aria-disabled="true"])');
      if (first) {
        event.preventDefault();
        allowMenuFocusRef.current = true;
        first.focus();
      }
    }
  };

  const handleFocus = () => clearTimeout(blurTimerRef.current);

  const handleBlur = (event) => {
    if (freeText) return;
    const input = inputNode();
    const target = event.relatedTarget;
    const menu = input && document.body.querySelector(`[role="menu"][aria-labelledby="${input.id}"]`);
    if (target && menu && menu.contains(target)) return;
    clearTimeout(blurTimerRef.current);
    blurTimerRef.current = setTimeout(() => {
      if (valueRef.current || document.activeElement === inputNode()) return;
      clearTimeout(timerRef.current);
      currentTermRef.current = '';
      setResults(null);
      setOpen(false);
      setText('');
    }, BLUR_CLEAR_MS);
  };

  const handleMenuFocus = () => {
    if (allowMenuFocusRef.current) return;
    const input = inputNode();
    if (input) input.focus();
  };

  const inputValue = freeText ? (value || '') : (value ? value.label : text);

  const resultItems = results && results.length > 0
    ? results.map((option) => ({
      key: String(option.value),
      label: option.label,
      description: option.description,
      onClick: () => pick(option),
    }))
    : [NO_MATCHES];

  const term = text.trim();
  const exactMatch = (results || []).some((option) => (option.label || '').trim().toLowerCase() === term.toLowerCase());
  const items = onCreate && !freeText && term !== '' && !exactMatch
    ? [
      ...resultItems,
      { key: 'create-divider', divider: true },
      { key: 'create', label: createLabel(term), icon: Plus, onClick: () => create(term) },
    ]
    : resultItems;

  return (
    <div ref={wrapperRef} className="grants-search-picker">
      <Menu
        open={open && results !== null}
        onOpenChange={setOpen}
        onFocus={handleMenuFocus}
        items={items}
        trigger={(
          <TextInput
            label={label}
            placeholder={placeholder}
            required={required}
            hint={hint}
            value={inputValue}
            autoComplete="off"
            onChange={handleChange}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
        )}
      />
    </div>
  );
};

SearchPicker.propTypes = {
  label: PropTypes.node,
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({ value: PropTypes.any, label: PropTypes.string }),
  ]),
  onChange: PropTypes.func.isRequired,
  search: PropTypes.func.isRequired,
  freeText: PropTypes.bool,
  required: PropTypes.bool,
  hint: PropTypes.node,
  onCreate: PropTypes.func,
  createLabel: PropTypes.func,
};

SearchPicker.defaultProps = {
  label: undefined,
  placeholder: undefined,
  value: null,
  freeText: false,
  required: false,
  hint: undefined,
  onCreate: undefined,
  createLabel: defaultCreateLabel,
};

export default SearchPicker;
